import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Drawer,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { SERVER_BASE_URL } from "../Config";

interface Notification {
  id: number;
  orderId: number;
  type: string;
  created: string;
  patientName: string;
  providerName: string;
  read: boolean;
}

interface NotificationBellProps {
  isPharmacy: boolean;
  tokenKey: string;
}

const TYPE_LABELS: Record<string, string> = {
  new_order: "New Order",
  cancellation_request: "Cancellation Requested",
  clarification_needed: "Clarification Needed",
  received_by_pharmacy: "Order Received",
};

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ isPharmacy, tokenKey }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem(tokenKey);
      if (!token) return;
      const res = await fetch(`${SERVER_BASE_URL}/notifications?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, [tokenKey]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 3000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem(tokenKey);
      if (!token) return;
      await fetch(`${SERVER_BASE_URL}/notifications/mark-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ all: true }),
      });
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error("Failed to mark notifications read:", err);
    }
  };

  const isOpen = isMobile ? drawerOpen : Boolean(anchorEl);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (isOpen) {
      setAnchorEl(null);
      setDrawerOpen(false);
    } else {
      if (isMobile) {
        setDrawerOpen(true);
      } else {
        setAnchorEl(event.currentTarget);
      }
      markAllRead();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setDrawerOpen(false);
  };

  const handleDismiss = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    try {
      const token = localStorage.getItem(tokenKey);
      if (!token) return;
      await fetch(`${SERVER_BASE_URL}/notifications/dismiss`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notificationId }),
      });
    } catch (err) {
      console.error("Failed to dismiss notification:", err);
      fetchNotifications();
    }
  };

  const handleDismissAll = async () => {
    const prev = notifications;
    setNotifications([]);
    setUnreadCount(0);
    try {
      const token = localStorage.getItem(tokenKey);
      if (!token) return;
      await fetch(`${SERVER_BASE_URL}/notifications/dismiss`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ all: true }),
      });
    } catch (err) {
      console.error("Failed to dismiss all notifications:", err);
      setNotifications(prev);
    }
  };

  const handleNotificationClick = (n: Notification) => {
    handleClose();
    if (isPharmacy) {
      navigate(`/manage/${n.orderId}`);
    } else {
      navigate(`/history/${n.orderId}`);
    }
  };

  const header = (
    <Box
      sx={{
        px: 2.5,
        pt: isMobile ? 4 : 2.5,
        pb: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold">
        Notifications
      </Typography>
      {notifications.length > 0 && (
        <Button size="small" onClick={handleDismissAll} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
          Clear all
        </Button>
      )}
    </Box>
  );

  const notificationContent = notifications.length === 0 ? (
    <Box sx={{ py: 5, px: 3, textAlign: "center" }}>
      <NotificationsIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No notifications yet
      </Typography>
    </Box>
  ) : (
    <List disablePadding sx={{ maxHeight: isMobile ? 350 : 360, overflow: "auto" }}>
      {notifications.map((n) => (
        <ListItemButton
          key={n.id}
          onClick={() => handleNotificationClick(n)}
          sx={{
            bgcolor: n.read ? "transparent" : "action.hover",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            px: 2,
          }}
        >
          <ListItemText
            primary={
              <Typography variant="body2" fontWeight={n.read ? "normal" : "bold"}>
                {TYPE_LABELS[n.type] || n.type} - {n.patientName}
              </Typography>
            }
            secondary={timeAgo(n.created)}
          />
          <IconButton
            size="small"
            onClick={(e) => handleDismiss(e, n.id)}
            sx={{ ml: 1, color: "text.disabled", "&:hover": { color: "text.primary" } }}
            aria-label="dismiss"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <>
      <IconButton
        sx={{ color: "#1a237e" }}
        aria-label="notifications"
        onClick={handleToggle}
      >
        <Badge
          color="error"
          variant="dot"
          invisible={unreadCount === 0}
        >
          <NotificationsIcon style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }} />
        </Badge>
      </IconButton>

      {isMobile ? (
        <Drawer
          anchor="top"
          open={drawerOpen}
          onClose={handleClose}
          sx={{ zIndex: 1302 }}
          PaperProps={{
            sx: {
              maxHeight: "70vh",
              marginTop: "72px",
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
            },
          }}
        >
          {header}
          {notificationContent}
        </Drawer>
      ) : (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: { sx: { width: 360, borderRadius: 2, mt: 0.5 } },
          }}
        >
          {header}
          {notificationContent}
        </Popover>
      )}
    </>
  );
};

export default NotificationBell;
